import groupBy from 'lodash/groupBy';
import minBy from 'lodash/minBy';

import type {
  OpportunityRelatedBundledExperience,
  OpportunityRelatedDeal,
} from '~/hooks/apis/offers/useGetOpportunityRelated';

import { BASIC_CATEGORY_INDEX, MAX_LOS, MIN_LOS, PACKAGE_CATEGORIES } from './packageCategories';
import type { RoomAssetLink } from './types';

export const PACKAGE_ROLES = ['base', 'upgrade', 'solo', 'soloUpgrade', 'skip'] as const;

export type PackageRole = (typeof PACKAGE_ROLES)[number];

export type PackageRoleChoice = { role: PackageRole; suffix: string };

export type PackageRoleChoices = Record<DealOptionGroup['key'], PackageRoleChoice>;

export type DealOptionGroup = {
  key: string;
  asset: Required<RoomAssetLink>;
  name: string;
  bundled: boolean;
  dealsByLos: Record<number, Array<OpportunityRelatedDeal>>;
};

// Groups given the same role and suffix copy as one package, which absorbs names that drift across stays.
export function packageKey(assetId: string, { role, suffix }: PackageRoleChoice): string {
  return `${assetId}::${role}::${role === 'base' ? '' : suffix.trim().toLowerCase()}`;
}

export type PackageVariantError =
  | { reason: 'noBase'; assetId: string; assetName: string }
  | {
      reason: 'overlappingStays';
      assetId: string;
      assetName: string;
      packageKey: string;
      role: PackageRole;
      suffix: string;
      nights: number;
    }
  | { reason: 'noSuffix'; assetId: string; assetName: string; groupKey: string; groupName: string };

export const { defaultOccupancy: DEFAULT_OCCUPANCY } = PACKAGE_CATEGORIES[BASIC_CATEGORY_INDEX];
const OCCUPANCY = /\b[1-9]\s*a(\s*[+&,]?\s*[0-9]\s*[cti])*(?![a-z0-9])/i;
const SOLO_WORDS = /\b(solo|single|one\s+(adult|person|guest)|1\s+(adult|person|guest))\b/i;
const SEVERAL_ADULTS_WORDS = /\b[2-9]\s+adults?\b/i;

// Salesforce's occupancy fields are almost never filled, so it is read from the Deal Option name. An explicit
// one ("2A", "1A 1C", "2A + 1C") always decides; "single" also names bedding ("2 Single Beds 2A"), so solo
// words only count when there is none.
export function occupancyOf(name: string | null | undefined): string | undefined {
  const explicit = name
    ?.match(OCCUPANCY)?.[0]
    .replace(/[^0-9a-z]/gi, '')
    .toUpperCase();
  if (explicit) {
    return explicit;
  }
  return name && SOLO_WORDS.test(name) && !SEVERAL_ADULTS_WORDS.test(name) ? '1A' : undefined;
}

const isSolo = (group: DealOptionGroup) => occupancyOf(group.name) === '1A';
const occupancyOfGroup = (group: DealOptionGroup) => occupancyOf(group.name) ?? DEFAULT_OCCUPANCY;

function stripLengthOfStay(name: string, nights: number): string {
  return name
    .replace(new RegExp(`\\b${nights}\\s*(n|nts?|nights?)\\b`, 'i'), '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function groupPreviousDealOptions(
  deals: Array<OpportunityRelatedDeal> = [],
  bundledExperiences: Array<OpportunityRelatedBundledExperience> = [],
): Array<DealOptionGroup> {
  const bundledDealIds = new Set(bundledExperiences.map((experience) => experience.deal_option__c).filter(Boolean));
  const groups = new Map<string, DealOptionGroup>();

  for (const deal of deals) {
    const sfid = deal.room_asset?.sfid;
    const assetName = deal.room_asset?.name;
    const nights = Number(deal.number_of_nights__c);

    if (!sfid || !assetName || !(Number(deal.price__c) > 0)) {
      continue;
    }
    if (!Number.isInteger(nights) || nights < MIN_LOS || nights > MAX_LOS) {
      continue;
    }

    const name = stripLengthOfStay(String(deal.name ?? ''), nights);
    const bundled = bundledDealIds.has(String(deal.id));
    // A bundled Deal Option never shares a group with its base, even when Salesforce names them alike.
    const key = `${sfid}::${bundled ? 'bundled' : ''}::${name.toLowerCase()}`;
    const group = groups.get(key) ?? { key, asset: { id: sfid, sfid, name: assetName }, name, bundled, dealsByLos: {} };
    group.dealsByLos[nights] = [...(group.dealsByLos[nights] ?? []), deal];
    groups.set(key, group);
  }

  return [...groups.values()];
}

function lowestSellPerNight({ dealsByLos }: DealOptionGroup): number {
  return Math.min(
    ...Object.entries(dealsByLos).flatMap(([nights, deals]) =>
      deals.map((deal) => Number(deal.price__c) / Number(nights)),
    ),
  );
}

function suffixFrom(name: string, baseName: string): string {
  const words = name.split(' ');
  const baseWords = baseName.split(' ');
  const sameWord = (word: string, baseWord?: string) => word.toLowerCase() === baseWord?.toLowerCase();
  let sharedStart = 0;
  while (sharedStart < words.length && sameWord(words[sharedStart], baseWords[sharedStart])) {
    sharedStart += 1;
  }
  let sharedEnd = 0;
  while (
    sharedEnd < words.length - sharedStart &&
    sharedEnd < baseWords.length - sharedStart &&
    sameWord(words[words.length - 1 - sharedEnd], baseWords[baseWords.length - 1 - sharedEnd])
  ) {
    sharedEnd += 1;
  }
  return words
    .slice(sharedStart, words.length - sharedEnd)
    .join(' ')
    .replace(/^[\s\-–,/&+]+|[\s\-–,/&+]+$/g, '');
}

function groupsPerRoom(groups: Array<DealOptionGroup>): Array<Array<DealOptionGroup>> {
  return Object.values(groupBy(groups, (group) => group.asset.id));
}

export function roomsWithVariants(groups: Array<DealOptionGroup>): Array<Array<DealOptionGroup>> {
  return groupsPerRoom(groups).filter(
    (roomGroups) => roomGroups.length > 1 || roomGroups.some((group) => group.bundled),
  );
}

export function hasPackageVariants(groups: Array<DealOptionGroup>): boolean {
  return roomsWithVariants(groups).length > 0;
}

function prefillRole(group: DealOptionGroup, base: DealOptionGroup | undefined): PackageRole {
  if (group === base) {
    return 'base';
  }
  if (group.bundled) {
    return 'skip';
  }
  if (isSolo(group)) {
    return 'solo';
  }
  // Extra guests are priced in EGS, so another occupancy is not an upgrade.
  return base && occupancyOfGroup(group) !== occupancyOfGroup(base) ? 'skip' : 'upgrade';
}

export function prefillPackageRoles(groups: Array<DealOptionGroup>): PackageRoleChoices {
  const choices: PackageRoleChoices = {};

  for (const roomGroups of groupsPerRoom(groups)) {
    const unbundled = roomGroups.filter((group) => !group.bundled);
    const notSolo = unbundled.filter((group) => !isSolo(group));
    const atDefaultOccupancy = notSolo.filter((group) => occupancyOfGroup(group) === DEFAULT_OCCUPANCY);
    const base =
      minBy(atDefaultOccupancy, lowestSellPerNight) ??
      minBy(notSolo, lowestSellPerNight) ??
      minBy(unbundled, lowestSellPerNight);

    for (const group of roomGroups) {
      const role = prefillRole(group, base);
      choices[group.key] = {
        role,
        suffix: role === 'base' ? '' : base ? suffixFrom(group.name, base.name) : group.name,
      };
    }
  }

  return choices;
}

// Upgrade and Solo rows pair with the room's Base row, and each price cell comes from one Deal Option.
export function packageVariantErrors(
  groups: Array<DealOptionGroup>,
  choices: PackageRoleChoices,
): Array<PackageVariantError> {
  const errors: Array<PackageVariantError> = [];

  for (const roomGroups of groupsPerRoom(groups)) {
    const { id: assetId, name: assetName } = roomGroups[0].asset;
    const copied = roomGroups.flatMap((group) => {
      const choice = choices[group.key];
      return choice && choice.role !== 'skip' ? [{ group, choice }] : [];
    });

    if (copied.length > 0 && !copied.some(({ choice }) => choice.role === 'base')) {
      errors.push({ reason: 'noBase', assetId, assetName });
    }

    const packages = new Map<string, { choice: PackageRoleChoice; stays: Array<number> }>();
    for (const { group, choice } of copied) {
      const key = packageKey(assetId, choice);
      const merged = packages.get(key);
      const stays = [...(merged?.stays ?? []), ...Object.keys(group.dealsByLos).map(Number)];
      packages.set(key, { choice: merged?.choice ?? choice, stays });
      if (choice.role !== 'base' && !choice.suffix.trim()) {
        errors.push({ reason: 'noSuffix', assetId, assetName, groupKey: group.key, groupName: group.name });
      }
    }
    for (const [key, { choice, stays }] of packages) {
      const clash = stays.find((stay, index) => stays.indexOf(stay) !== index);
      if (clash !== undefined) {
        errors.push({
          reason: 'overlappingStays',
          assetId,
          assetName,
          packageKey: key,
          role: choice.role,
          suffix: choice.suffix.trim(),
          nights: clash,
        });
      }
    }
  }

  return errors;
}
